import {
    put,
    call,
    select,
    takeEvery,
    takeLatest,
    take,
    all,
    fork
} from 'redux-saga/effects';
import { Set as ISet } from 'immutable';
import { RETRIEVED_BRAND_CONFIG } from 'capi/redux';

import {
    getSelfBuyEnabled,
    hasClassicCatalogs as getHasClassicCatalogs,
    getLoyaltyProgramValidator
} from '../brand/brandSelectors';
import { SET_PROGRAM_CODE, clearProgram, setProgramCode } from '../program/programModule';
import { SET_CART_STATUS, CART_STATUS } from '../cart/cartModule';
import { resetFieldValues, changeValue } from '../item/newItemFormModule';
import { getPreferredValues as getUnvalidatedPreferredValues } from '../app/bootstrap';
import {
    AMOUNT_FIELD,
    RECIPIENT_NAME_FIELD,
    SENDER_NAME_FIELD,
    RECIPIENT_EMAIL_FIELD,
    IS_PLASTIC_FIELD,
    FACEPLATE_FIELD,
    IS_SELFBUY_FIELD,
    CURRENCY_FIELD,
    PROGRAM_CODE
} from '../item/newItemForm';
import {
    validateProgramCode,
    validateName,
    validateEmail,
    validateFaceplateCode,
    validateAmount,
    validateCardType,
    validateBillingFirstName,
    validateBillingLastName,
    validateLoyaltyNumber,
    validateSelfBuy,
    validateClassicGift
} from '../validate/preferredValuesValidators';
import { formatAmountToString } from '../utils/numberUtils';
import { CARD_TYPE } from '../cardType/cardTypeModule';
import { LOYALTY_FIELD } from '../loyalty/loyaltyModule';
import { ADDRESS_FIRSTNAME, ADDRESS_LASTNAME } from '../address/addressModule';
import { SENDER_EMAIL_FIELD } from '../payment/paymentConstants';
import { getActiveProgramCode } from '../program/programSelectors';
import { logError } from '../utils/errorUtils';

import {
    getCartPath,
    getErrorNoRetryPath,
    getConfirmationPath,
    APP_ERROR_TYPES
} from './flow';
import {
    flowParamsUpdated,
    RESTART_BUY_FLOW,
    SHOW_APP_ERROR,
    ORDER_COMPLETE
} from './flowModule';
import {
    flowStateInitialized,
    setPreferredValue,
    updateFlowProgress,
    setPreferredPaymentValue,
    removePreferredValue,
    PREFERRED_CLASSIC_GIFT
} from './flowProgressModule';
import {
    getHomePath,
    getPreferredClassicGift
} from './flowSelectors';
import {
    getURLSearchParams,
    push,
    removeQueryParams,
    replace,
    updateSearch
} from './routing';
import { LOCATION_CHANGE } from './routingModule';



export function* updateProgramCode({ programCode }) {
    const urlParams = yield call(getURLSearchParams);
    const urlProgramCode = yield call([urlParams, urlParams.get], 'programCode');
    const preferredClassicGift = yield select(getPreferredClassicGift);
    if (preferredClassicGift && programCode) {
        yield call(removeQueryParams, ['programCode']);
        yield put(removePreferredValue(PROGRAM_CODE));
    } else if (programCode) {
        if (urlProgramCode !== programCode) {
            yield call(updateSearch, { programCode });
        }
    } else if (urlProgramCode !== null) {
        yield call(removeQueryParams, ['programCode']);
    }
    if (!programCode) {
        yield put(removePreferredValue(PROGRAM_CODE));
    }
}


export function* updateQueryParams() {
    
    
    
    
    const programCode = yield select(getActiveProgramCode);
    yield call(updateProgramCode, { programCode });
    yield put(flowParamsUpdated());
}


export function* updateCartStatus({ status }) {
    
    if (status === CART_STATUS.PROCESSING || status === CART_STATUS.READY) {
        yield call(push, ({ pathname: getCartPath() }));
    }
}


export function* restartBuyFlow() {
    const homePath = yield select(getHomePath);
    yield put(clearProgram());
    yield put(resetFieldValues());
    yield call(push, ({ pathname: homePath }));
}


export function* showAppError({ errorType }) {
    if (errorType === APP_ERROR_TYPES.NO_RETRY) {
        yield call(replace, { pathname: getErrorNoRetryPath() });
    }
}


export function* completeBuyFlow({ orderDetails }) {
    
    
    
    const location = {
        pathname: getConfirmationPath(),
        state: orderDetails
    };
    yield call(replace, location);
}

function* handlePreferredValue(preferredValue = null, options) {
    const {
        storeKey,
        validator,
        formatter,
        onUpdate,
        isPaymentValue,
        skipUpdateProgress
    } = options;

    let processedPreferredValue = preferredValue;

    if (processedPreferredValue !== null && validator) {
        processedPreferredValue = yield call(validator, preferredValue);
    }

    if (processedPreferredValue !== null && formatter) {
        processedPreferredValue = yield call(formatter, processedPreferredValue);
    }

    if (processedPreferredValue !== null) {
        if (isPaymentValue) {
            
            yield put(setPreferredPaymentValue(storeKey, processedPreferredValue));
        } else {
            
            yield put(setPreferredValue(storeKey, processedPreferredValue));

            
            yield put(changeValue(storeKey, processedPreferredValue));
        }

        if (!skipUpdateProgress) {
            yield put(updateFlowProgress(ISet([storeKey])));
        }

        if (onUpdate) {
            yield call(onUpdate, processedPreferredValue);
        }
    }
}


export function* initializeFlow() {
    
    

    
    
    const actionsToAwait = {
        catalog: take(RETRIEVED_BRAND_CONFIG.CATALOG),
        preferences: take(RETRIEVED_BRAND_CONFIG.PREFERENCES)
    };

    try {
        yield all(actionsToAwait);

        const preferredValues = yield call(getUnvalidatedPreferredValues);

        
        const selfBuyEnabled = yield select(getSelfBuyEnabled);
        const hasClassicCatalogs = yield select(getHasClassicCatalogs);
        const loyaltyNumberRegex = yield select(getLoyaltyProgramValidator);

        

        
        const preferredValuesOptions = {
            currencyCode: {
                storeKey: CURRENCY_FIELD,
                onUpdate: function* onUpdate() {
                    yield call(removeQueryParams, ['currencyCode', 'currency_code']);
                }
            },
            recipientName: {
                storeKey: RECIPIENT_NAME_FIELD,
                validator: validateName
            },
            senderName: {
                storeKey: SENDER_NAME_FIELD,
                validator: validateName
            },
            recipientEmail: {
                storeKey: RECIPIENT_EMAIL_FIELD,
                validator: validateEmail
            },
            amount: {
                storeKey: AMOUNT_FIELD,
                validator: validateAmount,
                formatter: formatAmountToString
            },
            cardType: {
                storeKey: IS_PLASTIC_FIELD,
                validator: validateCardType,
                formatter: preferredValue => preferredValue === CARD_TYPE.PHYSICAL
            },
            faceplateCode: {
                storeKey: FACEPLATE_FIELD,
                validator: validateFaceplateCode
            },
            selfBuy: {
                storeKey: IS_SELFBUY_FIELD,
                validator: preferredValue => validateSelfBuy(preferredValue, selfBuyEnabled)
            },
            classicGift: {
                storeKey: PREFERRED_CLASSIC_GIFT,
                validator: preferredValue => validateClassicGift(preferredValue, hasClassicCatalogs)
            },
            programCode: {
                storeKey: PROGRAM_CODE,
                validator: validateProgramCode,
                onUpdate: function* onUpdate(preferredValue) {
                    yield put(setProgramCode(preferredValue));
                }
            },
            billingFirstName: {
                storeKey: ADDRESS_FIRSTNAME,
                validator: validateBillingFirstName,
                isPaymentValue: true,
                skipUpdateProgress: true
            },
            billingLastName: {
                storeKey: ADDRESS_LASTNAME,
                validator: validateBillingLastName,
                isPaymentValue: true,
                skipUpdateProgress: true
            },
            billingPurchaserEmail: {
                storeKey: SENDER_EMAIL_FIELD,
                validator: validateEmail,
                isPaymentValue: true,
                skipUpdateProgress: true
            },
            loyaltyNumber: {
                storeKey: LOYALTY_FIELD,
                validator: preferredValue => validateLoyaltyNumber(loyaltyNumberRegex, preferredValue),
                isPaymentValue: true,
                skipUpdateProgress: true
            }
        };

        
        
        for (const [fieldName, options] of Object.entries(preferredValuesOptions)) {
            const preferredValue = preferredValues[fieldName];

            
            if (typeof preferredValue !== 'undefined' && preferredValue !== null) {
                yield call(handlePreferredValue, preferredValue, options);
            }
        }
    } catch (error) {
        logError(`There was an error in initializeFlow: ${error}`);
    } finally {
        
        
        yield put(flowStateInitialized());
    }
}

export function* flowSagasWatcher() {
    yield takeEvery(LOCATION_CHANGE, updateQueryParams);
    yield takeEvery(SET_PROGRAM_CODE, updateProgramCode);
    yield takeLatest(SET_CART_STATUS, updateCartStatus);
    yield takeLatest(RESTART_BUY_FLOW, restartBuyFlow);
    yield takeLatest(ORDER_COMPLETE, completeBuyFlow);
    yield takeLatest(SHOW_APP_ERROR, showAppError);

    yield fork(initializeFlow);
}
